import { LinePriceDetails } from '../../../../../components/lines-editor/base-doc-line-editor/base-doc-line-editor';
import { InvoiceTagComponent } from './../../../../../components/invoice-tags/invoice-tag.component';
import { DocHierarchyComponent } from './../../../../../components/doc-hierarchy/doc-hierarchy.component';
import { DocLinkComponent } from '../../../../../components/doc-link/doc-link.component';
import { ProductionFeatureDefinition } from '@digiwall/goliath-products/config';
import { CommonModule, CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  Input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  CustomerInvoiceLookupDto,
  CustomerProjectLookUpDto,
  CustomerProjectService,
  Deliverable,
  DocLinkDto,
  Reserved,
  ThirdPartyDocDto,
} from '@digiwall/goliath-documents/proxy';
import { LocalizationModule, LocalizationService } from '@abp/ng.core';
import { KpiComponent, KpiGridComponent, PluralizePipe } from '@digiwall/ui-kit';
import {
  CurrentCompanyStore,
  InformationsAuditedEntityComponent,
  NewId,
} from '@digiwall/goliath-core';
import { DocRole, WeightUnit, LocalityDto, LocalityService } from '@digiwall/goliath-core/proxy';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AccountingCustomerOutstandingResponseDto,
  AddressDto,
  ContactDto,
  ThirdPartyDocCountDto,
  ThirdPartyDto,
} from '@digiwall/goliath-contacts/proxy';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  AccountingBalanceSummaryComponentComponent,
  FinancialScoreImageComponent,
} from '@digiwall/goliath-contacts';
import { DocSidePanelTagsComponent } from '../side-panel-tags/side-panel-tags.component';
import { DocSidePanelExplanationsComponent } from '../side-panel-explanations/side-panel-explanations.component';
import { format } from 'date-fns';
import { DEFAULT_DATETIME_FORMAT_SHORT_DATE_PATTERN } from '@digiwall/core';
import { DocIconAddressComponent } from '../doc-icon-address/doc-icon-address.component';

@Component({
  selector: 'document-side-panel',
  templateUrl: './doc-side-panel.component.html',
  styleUrl: './doc-side-panel.component.scss',
  imports: [
    CommonModule,
    LocalizationModule,
    KpiGridComponent,
    KpiComponent,
    InformationsAuditedEntityComponent,
    AccountingBalanceSummaryComponentComponent,
    DocSidePanelTagsComponent,
    DocHierarchyComponent,
    DocLinkComponent,
    DocSidePanelExplanationsComponent,
    DecimalPipe,
    CurrencyPipe,
    PluralizePipe,
    DatePipe,
    FinancialScoreImageComponent,
    InvoiceTagComponent,
    DocIconAddressComponent,
  ],
})
export class DocSidePanelComponent {
  _entity: ThirdPartyDocDto;
  get entity(): ThirdPartyDocDto &
    Partial<{
      totalDeposit: number;
      balanceDue: number;
      isDeliverable: Deliverable;
      isReserved: Reserved;
      customerProjectId?: string;
    }> {
    return this._entity;
  }
  @Input() set entity(value: ThirdPartyDocDto) {
    this._entity = value;
    if (this.entity != null && this.entity.id != null && this.entity.id != NewId) {
      this.setFinancialInfos();
      this.setDocLinkDtosObservable();
    }
  }

  docService = input<any>();
  @Input() protected linePricingDetails: LinePriceDetails;
  @Input() protected remainingAmount$: Observable<number>;
  @Input() protected invoices$: Observable<CustomerInvoiceLookupDto[]>;
  @Input() protected thirdPartyContact$: BehaviorSubject<string>;
  @Input() protected contactEmail: string;
  @Input() protected contactPhone: string;
  @Input() protected customerAddress: string;
  @Input() protected accountingCustomerOutstandingDto: AccountingCustomerOutstandingResponseDto;
  @Input() protected thirdParty: ThirdPartyDto;
  @Input() protected creditLimit: number = 0;
  @Input() protected thirdPartyDocLinkedCount: ThirdPartyDocCountDto;
  @Input() protected isDueDateExpired = false;
  protected isStockValidatedWarning = input<boolean>(false);
  protected deliveryAddressForm = input<AddressDto>();
  protected lastNotificationDate = input<string | null>();
  protected customerProjectId = input<string | null>();

  @Output() redirectToThirdPartyEvent = new EventEmitter<{
    thirdPartyId: string;
    addContact: boolean;
  }>();

  protected readonly currentCompanyStore = inject(CurrentCompanyStore);
  protected readonly customerProjectService = inject(CustomerProjectService);
  protected readonly productionFeature = inject(ProductionFeatureDefinition);

  deliveryAddressLabel = computed(() => {
    let locality = this.localitySignal();
    const parts = [
      this.deliveryAddressForm()?.street,
      locality?.postalCode,
      locality?.city?.current,
    ];
    return parts.filter(x => x?.length > 0).join(' ');
  });

  @ViewChild('docHierarchy') docHierarchy: DocHierarchyComponent;
  thirdPartyContacts$ = new BehaviorSubject<ContactDto[]>([]);

  weightUnits = WeightUnit;
  docRole = DocRole;
  Deliverable = Deliverable;
  Reserved = Reserved;

  financialInfos: string;

  workingSiteLabel = 'Doc::WorkingSiteAddress';
  pointOfSaleLabel = 'Doc::PointOfSaleAddress';
  customerLabel = 'Doc::CustomerAddress';

  localitySignal = signal<LocalityDto>(null);
  docLinkDtosObservable = signal<Observable<DocLinkDto[]>>(null);
  protected readonly productionLinkDtosObservable = signal<Observable<DocLinkDto[]>>(null);

  protected customerProject: CustomerProjectLookUpDto;

  get currentDocRole() {
    return this.entity?.role;
  }

  constructor(
    private localizationService: LocalizationService,
    private router: Router,
    protected route: ActivatedRoute,
    private localityService: LocalityService,
  ) {
    effect(() => {
      if (this.deliveryAddressForm()?.localityId != null) {
        this.localityService.get(this.deliveryAddressForm().localityId).subscribeOnce(locality => {
          this.localitySignal.set(locality);
        });
      }
    });

    effect(() => {
      if (this.docService()) {
        this.setDocLinkDtosObservable();
      }
    });

    effect(() => this.setCustomerProject());
  }

  protected redirectToThirdParty(thirdPartyId: string = NewId, addContact: boolean = false) {
    this.redirectToThirdPartyEvent.emit({ thirdPartyId, addContact });
  }

  private setFinancialInfos() {
    const date = new Date(this.entity.thirdPartyFinancialDataLastUpdateDate);
    const dateWithFormat = format(
      new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())),
      DEFAULT_DATETIME_FORMAT_SHORT_DATE_PATTERN,
    );
    this.financialInfos =
      this.entity.thirdPartyFinancialStatus +
      '</br>' +
      this.localizationService.instant('GoliathCore::LastUpdate') +
      ': ' +
      dateWithFormat;
  }

  viewInvoice(invoiceId: string) {
    this.router.navigate(['../../customer-invoices', invoiceId], { relativeTo: this.route });
  }

  protected navigateToDocumentList(docRole: string): void {
    const baseUrl = window.location.href.replace(this.router.url, '');
    window.open(
      baseUrl + `/documents/${docRole}?thirdPartyCode=${this.entity.thirdPartyCode}`,
      '_blank',
    );
  }

  public refreshHierarchy(force: boolean = false): void {
    this.docHierarchy?.loadHierarchy(force);
  }

  protected setDocLinkDtosObservable() {
    if (this.docService() && this.entity?.id) {
      if (this.docService().getSupplierPurchaseOrderLinks != null) {
        this.docLinkDtosObservable.set(
          this.docService().getSupplierPurchaseOrderLinks(this.entity.id),
        );
      } else if (this.docService().getCustomerSalesPurchaseLinks != null) {
        this.docLinkDtosObservable.set(
          this.docService().getCustomerSalesPurchaseLinks(this.entity.id),
        );
      }

      if (this.productionFeature.isEnabled && this.docService().getProductionFolderLinks != null) {
        this.productionLinkDtosObservable.set(
          this.docService().getProductionFolderLinks(this.entity.id),
        );
      }
    }
  }

  protected setCustomerProject() {
    if (!this.customerProjectId()) {
      this.customerProject = null;
      return;
    }

    this.customerProjectService
      .getLookupList({ ids: [this.customerProjectId()], queryProperties: [], sorting: [] })
      .subscribeOnce(x => {
        if (x.items?.length > 0) {
          this.customerProject = x.items[0];
        }
      });
  }

  protected navigateToCustomerProject() {
    if (!this.customerProjectId()) return;
    const baseUrl = window.location.href.replace(this.router.url, '');
    window.open(baseUrl + `/documents/customer-projects/${this.customerProjectId()}`, '_blank');
  }
}
